import { Button, FormControl, Grid, TextField, Typography, useTheme } from "@mui/material";
import {
  LanguageConsumer,
  getTranslation,
} from "../../../../../../Infrastructure/Internationalisation/TranslationService";
import { CustomBox } from "../../../../../../Layout/CustomBox";
import { TextFieldFormControl } from "../../../../../../Layout/Inputs/TextFieldFormControl";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { fetchMyDepragApi } from "../../../../../../Infrastructure/Api/ApiBaseCall";
import { useAppSelector, useAppDispatch } from "../../../../../../Infrastructure/Store/hooks";
import { store, RootState } from "../../../../../../Infrastructure/Store/store";
import { MaintenanceMessage } from "../../Overview/Models/MaintenanceMessage";
import { MaintenanceMessagesValidator } from "../Models/MaintenanceMessagesValidator";
import { EmptyMessage } from "../Models/EmptyMaintenanceMessage";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment, { Moment } from "moment";
import { NavLink, useNavigate } from "react-router-dom";
import { MaintenanceMessageAddResponseDto } from "../Models/MaintenanceMessageAddResponseDto";


export const MaintenanceMessageFormAdd = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkMode = useAppSelector((store) => store.settings.theme === "dark");
  const dispatch = useAppDispatch();
  const [validFrom, setValidFrom] = useState<Moment>(moment().startOf("hour"));
  const [validUntil, setValidUntil] = useState<Moment>(moment().startOf("hour").add(1, "day"));

  const [isLoading, setIsLoading] = useState(false);

  const {register, handleSubmit, formState: {errors}} = useForm({
    defaultValues: EmptyMessage,
    values: EmptyMessage,
    resolver: zodResolver(MaintenanceMessagesValidator),
  });

  const onSubmit = async (dataToSend: MaintenanceMessage) => {
    setIsLoading(true);
    try {
      const response = await fetchMyDepragApi<MaintenanceMessageAddResponseDto>(
        {
          ...dataToSend,
          ValidFrom: validFrom.toDate(),
          ValidUntil: validUntil.toDate(),
        } as MaintenanceMessage,
        "highlevel/maintenance/add/",
        "POST",
        dispatch,
        store.getState() as RootState,
        getTranslation("HighLevelMaintenanceMessageSuccessAdd"),
      );
      if (response) navigate(`/highlevel/maintenance/details/${response.ResponseId}`);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LanguageConsumer>
      {({getTranslatedText}) =>
        <Grid container direction="row" spacing={2} className="main-content" sx={{mt: 1}}>
          <Grid item xs={12} sx={{mb: 2, mx: 1}}>
            <Button color="darky" component={NavLink} to={"/highlevel/maintenance"} variant="outlined">
              {getTranslatedText("Prev")}
            </Button>
          </Grid>

          <CustomBox
            elevation={0}
            sx={{
              m: 3,
              pt: 2,
              height: "100%",
              width: "100%",
              backgroundSize: "cover",
              backgroundBlendMode: "multiply",
              backgroundColor: isDarkMode ? "transparent" : "#fff",
              border: `1px solid ${theme.palette.cardBorder.main}`,
            }}
          >

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container direction={"row"} spacing={2} sx={{mt: 1}}>
                <Typography paddingLeft={2} fontWeight={700}>
                  {getTranslatedText("HighLevelMaintenanceMessageTitle")}
                </Typography>

                <Grid item xs={12} container direction={"row"} spacing={3}>
                  <Grid item xs={6}>
                    <TextFieldFormControl
                      inputProps={{
                        "data-testid": "maintenance-titleDe",
                      }}
                      labelText="DE"
                      propertyName="TitleDE"
                      register={register}
                      registerOptions={{valueAsNumber: false}}
                      errors={errors}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextFieldFormControl
                      inputProps={{
                        "data-testid": "maintenance-titleEn",
                      }}
                      labelText="EN"
                      propertyName="TitleEN"
                      register={register}
                      registerOptions={{valueAsNumber: false}}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container direction={"row"} spacing={2} sx={{mt: 1}}>
                <Typography paddingLeft={2} fontWeight={700}>
                  {getTranslatedText("HighLevelMaintenanceMessageDescription")}
                </Typography>

                <Grid item xs={12} container direction={"row"} spacing={3}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        inputProps={{
                          "data-testid": "maintenance-descriptionDe",
                        }}
                        multiline
                        maxRows={15}
                        label="DE"
                        id="TxtField_DescriptionDE"
                        error={!!errors["DescriptionDE"]}
                        helperText={errors["DescriptionDE"] ? getTranslatedText(errors["DescriptionDE"].message as string) : ""}
                        InputLabelProps={{shrink: true}}
                        {...register("DescriptionDE")}/>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <TextField
                        inputProps={{
                          "data-testid": "maintenance-descriptionEn",
                        }}
                        multiline
                        maxRows={15}
                        label="EN"
                        id="TxtField_DescriptionEN"
                        error={!!errors["DescriptionEN"]}
                        helperText={errors["DescriptionEN"] ? getTranslatedText(errors["DescriptionEN"].message as string) : ""}
                        InputLabelProps={{shrink: true}}
                        {...register("DescriptionEN")}/>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container direction={"row"} spacing={2} sx={{mt: 1}}>
                <Typography paddingLeft={2} fontWeight={700}>
                  {getTranslatedText("HighLevelMaintenanceMessageTiming")}
                </Typography>

                <Grid item xs={12} container direction={"row"} spacing={3}>
                  <Grid item xs={6}>
                    <DateTimePicker
                      maxDate={validUntil}
                      label={getTranslatedText("HighLevelMaintenanceMessageValidFrom")}
                      format="HH:mm DD/MM/YYYY"
                      defaultValue={validFrom}
                      value={validFrom}
                      onChange={(x) => x && setValidFrom(x)}
                    />

                  </Grid>

                  <Grid item xs={6}>
                    <DateTimePicker
                      minDate={validFrom}
                      label={getTranslatedText("HighLevelMaintenanceMessageValidUntil")}
                      format="HH:mm DD/MM/YYYY"
                      defaultValue={validUntil}
                      value={validUntil}
                      onChange={(x) => x && setValidUntil(x)}/>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container direction={"row"} spacing={2} sx={{mt: 2}}>
                <Grid item xs={12}>
                  <LoadingButton
                    type="submit"
                    color="success"
                    fullWidth
                    variant="contained"
                    loading={isLoading}
                  >
                    {getTranslatedText("CommonSave")}
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </CustomBox>
        </Grid>
      }
    </LanguageConsumer>
  );
};
